import dayjs from 'dayjs'
import { map, orderBy, set, size } from 'lodash/fp'
import { useMemo } from 'react'
import { Dataset, DatasetStatus } from '~/api/onPremAgent/datasetsAPI'
import Button from '~/components/buttons/Button'
import Table from '~/components/Table'
import { getInstrumentImage } from '~/utils/instrument'
import { renderStatusPill } from '~/utils/statusPill'
import cs from './datasets_table.scss'

interface DatasetRow extends Dataset {
  _link: string
}

interface DatasetsTableProps {
  datasets: Dataset[]
  onRetryDataset: (dataset: Dataset) => void
  retryingDatasetUuid?: string | null
}

// We should mark statuses that may indicate a connection issue to the cloud as warnings.
export const DATASET_STATUS_CONFIG = {
  in_progress: { type: 'default' },
  pending_finalization: { type: 'default' },
  // If the dataset is stuck here, it might mean there is a connection issue to the cloud.
  ready: { type: 'warning' },
  uploading: { type: 'accent' },
  uploaded: { type: 'success' },
  upload_failed: { type: 'error' },
  invalid: { type: 'error' },
}

export const getPlateBarcode = (dataset: Dataset): string | null => {
  return dataset.metadata?.plate_metadata?.plate_name || null
}

export const getRelativeDetectedTime = (dataset: Dataset): string | null => {
  return dayjs(dataset.created_at).fromNow()
}

export const getDatasetSummary = (dataset: Dataset): string | null => {
  const resultsByWell = dataset.structured_data?.results_by_well

  if (dataset.structured_data?.type === 'byonoy') {
    const firstItem = resultsByWell ? Object.values(resultsByWell)[0] : null
    return `${size(firstItem)} wells`
  }

  const imagingParameters =
    dataset.metadata?.result_metadata?.type === 'cytation' ||
    dataset.metadata?.result_metadata?.type === 'imagexpress' ||
    dataset.metadata?.result_metadata?.type === 'yokogawa_cq1'
      ? dataset.metadata.result_metadata.imaging_parameters
      : null

  if (!resultsByWell && !imagingParameters) {
    return null
  }

  if (!imagingParameters) {
    return `${size(resultsByWell)} wells`
  }

  return `${size(resultsByWell)} wells · ${imagingParameters.montage_rows}x${imagingParameters.montage_columns} montages`
}

const renderPlateBarcode = (row: DatasetRow) => {
  const plateBarcode = getPlateBarcode(row)
  if (!plateBarcode) {
    return <div className={cs.noData}>—</div>
  }
  return plateBarcode
}

const renderInstrument = (row: DatasetRow) => {
  if (!row.instrument_name || !row.instrument_type) {
    return <div className={cs.noData}>—</div>
  }
  return (
    <div className={cs.instrumentNameContainer}>
      <img
        className={cs.instrumentImage}
        src={getInstrumentImage({
          instrumentType: row.instrument_type,
          instrumentName: '',
        })}
      />
      <span className={cs.instrumentName}>{row.instrument_name}</span>
    </div>
  )
}

const renderDatasetSummary = (row: DatasetRow) => {
  return getDatasetSummary(row) || <div className={cs.noData}>—</div>
}

const renderDetectedTime = (timestamp: string) => {
  const time = dayjs(timestamp)
  return <div className={cs.time}>{time.fromNow()}</div>
}

const renderUploadStatus = (status: DatasetStatus) => {
  return renderStatusPill(status, DATASET_STATUS_CONFIG, {})
}

const shouldShowRetryButton = (dataset: DatasetRow) => {
  if (dataset.status !== 'invalid') {
    return false
  }
  const instrumentType = dataset.instrument_type?.toLowerCase() || ''
  return (
    instrumentType === 'imagexpress_micro_confocal' || instrumentType === 'yokogawa_cq1'
  )
}

const renderRetryButton = (
  row: DatasetRow,
  onRetryDataset: (dataset: Dataset) => void,
  retryingDatasetUuid?: string | null,
) => {
  if (!shouldShowRetryButton(row)) {
    return <div></div>
  }

  const handleClick = () => {
    onRetryDataset(row)
  }

  return (
    <Button
      label='Retry'
      variant='minimalBold'
      className={cs.retryButton}
      onClick={handleClick}
      disabled={retryingDatasetUuid === row.uuid}
    />
  )
}

const createTableColumns = (
  onRetryDataset: (dataset: Dataset) => void,
  retryingDatasetUuid?: string | null,
) => [
  {
    name: 'Plate Barcode',
    width: 250,
    render: renderPlateBarcode,
    oneLine: true,
  },
  {
    name: 'Dataset Summary',
    width: 200,
    render: renderDatasetSummary,
    smallText: true,
  },
  {
    name: 'Instrument',
    width: 200,
    render: renderInstrument,
  },
  {
    name: 'Upload Status',
    width: 200,
    render: (row: DatasetRow) => renderUploadStatus(row.status),
    verticalCenter: true,
  },
  {
    name: 'Detected Time',
    width: 150,
    render: (row: DatasetRow) => renderDetectedTime(row.created_at),
    rightAlign: true,
    smallText: true,
  },
  {
    name: '', // Retry button column. No header to make it as succinct as possible.
    width: 150,
    render: (row: DatasetRow) =>
      renderRetryButton(row, onRetryDataset, retryingDatasetUuid),
    rightAlign: true,
    stopPropagation: true,
  },
]

export default function DatasetsTable(props: DatasetsTableProps) {
  const { datasets, onRetryDataset, retryingDatasetUuid } = props
  const columns = useMemo(
    () => createTableColumns(onRetryDataset, retryingDatasetUuid),
    [onRetryDataset, retryingDatasetUuid],
  )
  const datasetRows = map(
    (dataset: Dataset) => set('_link', `/datasets/${dataset.uuid}`, dataset),
    orderBy('created_at', 'desc', datasets),
  ) as unknown as DatasetRow[]
  return (
    <Table<DatasetRow>
      columns={columns}
      data={datasetRows}
      rowKey='id'
      rowLeftRightPaddingVariant='rowPaddingNormal'
      headerTopBottomPaddingVariant='headerTopBottomPaddingEqual'
      heightSizing='default'
      stickyHeaders
    />
  )
}
